"use client";
import axios from "axios";
import fbTracker from "./fb-tracking.js";
export * from "./hooks/useBluelagoon";
export { useTaskWebSocket } from "./hooks/useTaskWebSocket";

/**
 * Mirrors apps/api/src/main.js#isWildcardCorsPath. Routes listed here return
 * Access-Control-Allow-Origin:* on the server; the browser refuses to accept
 * credentialed responses from a wildcard origin, so we must NOT set
 * withCredentials on requests that target these paths.
 *
 * Keep this in sync with the server — they're effectively two halves of the
 * same contract. Paths here are Bluelagoon-relative (no leading slash).
 */
function _isWildcardCorsRoute(relPath) {
    if (!relPath || typeof relPath !== "string") return false;
    if (relPath.startsWith("proxy/")) return true;
    if (relPath === "proxy-get") return true;
    if (relPath === "databases/records") return true;
    if (/^workflows\/[^/]+\/auth\//.test(relPath)) return true;
    // Webhook endpoints are ALB-offloaded to a Lambda that emits wildcard
    // CORS (webhook-handler.ts). Covers /webhook/:publicKey,
    // /webhook/pdt-private-key/:publicKey, /webhook/subscription/:publicKey.
    if (relPath.startsWith("webhook/")) return true;
    return false;
}

class Blue {
    isDevelopment = false;
    isStaging = false;
    apiUrl = "";
    proxyImageUrl = "";
    stripePublishableKey = "";
    webhookEndpoint = "";

    settings = {};
    constructor() {
        if (typeof window !== "undefined") {
            const hostname = window?.location?.hostname ?? "";
            this.isDevelopment =
                hostname === "localhost" ||
                hostname.includes("7ab7-45-73-144-242.ngrok-free.app");
            this.isStaging =
                hostname === "staging.agentui.ai" ||
                hostname === "staging-api.agentui.ai" ||
                hostname === "staging-apps.agentui.ai";

            if (this.isDevelopment) {
                this.apiUrl = "http://localhost:3333";
                this.proxyImageUrl =
                    "http://localhost:3333/proxy/image-signed-url";
                this.appUrl = "http://localhost:4200";
                this.componentServerEndpoint = "http://agentui.test:3334";
                this.stripePublishableKey =
                    "pk_test_51R4L2LKVJm3st1cQlSP0cnkxPhW3k4zvKN5hA32ywRdEXjefiyzZayMnutgGyMbSIXkmif9c3OM43jlaDM5z45ml00KlSaxOzW";
            } else if (this.isStaging) {
                this.apiUrl = "https://staging-api.agentui.ai";
                this.proxyImageUrl =
                    "https://staging-api.agentui.ai/proxy/image-signed-url";
                this.appUrl = `${
                    window?.location?.protocol ?? "https:"
                }//${hostname}`;
                this.componentServerEndpoint =
                    "https://staging-apps.agentui.ai";
                this.stripePublishableKey =
                    "pk_test_51R4L2LKVJm3st1cQlSP0cnkxPhW3k4zvKN5hA32ywRdEXjefiyzZayMnutgGyMbSIXkmif9c3OM43jlaDM5z45ml00KlSaxOzW";
            } else {
                this.apiUrl = "https://api.agentui.ai";
                this.proxyImageUrl =
                    "https://api.agentui.ai/proxy/image-signed-url";
                this.appUrl = `${window?.location?.protocol ?? "https:"}//${
                    hostname ?? "app.agentui.ai"
                }`;
                this.componentServerEndpoint = "https://apps.agentui.ai";
                this.stripePublishableKey =
                    "pk_live_51R4L2LKVJm3st1cQxICtMSobHj8ktxjjBcCUoP3tdS8mtZc3FvDBvmgNZ7OEzinsc4ekglvEzOt79UBaipBZAJg600uvpidnVu";
            }

            this.webhookEndpoint = `${this.apiUrl}/webhook/{publicKey}`;
            this.appEndpoint = `${this.appUrl}/public/{publicKey}`;
            this.settings = {
                whatsapp: {
                    appId: "1177581790190075",
                    configId: "1072892131721757",
                },
            };
        } else {
            this.isDevelopment = process?.env?.NODE_ENV === "development";
            this.apiUrl = process?.env?.API_URL ?? "http://localhost:3333";
            this.proxyImageUrl =
                process?.env?.API_URL ??
                "http://localhost:3333/proxy/image-signed-url";
        }
    }

    getComponentServerUrl(internalDomain, customDomain) {
        const isLocal = this.isDevelopment;
        const protocol = isLocal ? "http" : "https";
        const port = isLocal ? ":3334" : "";

        if (customDomain) {
            const domainPort =
                isLocal && !customDomain.includes("3334") ? port : "";
            return {
                componentServerUrl: `${protocol}://${customDomain}${domainPort}`,
                useCustomDomain: true,
            };
        }

        if (internalDomain) {
            const domainPort =
                isLocal && !internalDomain.includes("3334") ? port : "";
            return {
                componentServerUrl: `${protocol}://${internalDomain}${domainPort}`,
                useCustomDomain: true,
            };
        }

        return {
            componentServerUrl: this.componentServerEndpoint,
            useCustomDomain: false,
        };
    }

    call(method, path, params, options = {}) {
        const retryOverride = options?.retry;
        const maxRetries = Math.min(
            Math.max(1, Math.floor(options?.retryCount ?? 3)),
            10
        ); // Between 1 and 10, default 3

        // Internal function to make the actual request
        const makeRequest = () => {
            let paramsRequest = "";
            let headers = {
                "Content-Type": "application/json",
                ...(options?.headers ?? {}),
            };

            if (params && Object.keys(params).length > 0 && method === "get") {
                const queryParts = [];
                Object.keys(params).forEach((key) => {
                    if (params[key] != null) {
                        queryParts.push(`${key}=${params[key]}`);
                    }
                });
                if (queryParts.length > 0) {
                    paramsRequest = "?" + queryParts.join("&");
                }
            }

            const token = this.getToken();
            if (token) {
                headers = {
                    ...headers,
                    Authorization: `Bearer ${token}`,
                };
            }

            // Decide whether to send credentials based on the route. This
            // MIRRORS the server-side exclusion list in apps/api/src/main.js
            // (isWildcardCorsPath). Those routes return Access-Control-
            // Allow-Origin:*, which the browser refuses to combine with
            // credentials — so we must suppress credentials for them.
            //
            // All other routes return credentialed CORS (echo origin +
            // Allow-Credentials:true), which requires withCredentials on
            // the request for Set-Cookie to be stored on the response.
            const defaultWithCreds = !_isWildcardCorsRoute(path);

            return axios({
                method,
                url: `${this.apiUrl}/${path}${paramsRequest}`,
                data: params,
                headers,
                timeout: options?.timeout ?? 30000,
                withCredentials:
                    options?.withCredentials !== undefined
                        ? options.withCredentials
                        : defaultWithCreds,
            });
        };

        // If retry is explicitly set, respect it; otherwise auto-retry on 5xx/network errors
        const shouldRetryError = (error) => {
            if (retryOverride === false) return false;

            // 5xx server errors - always retry
            if (error.response?.status >= 500) {
                return true;
            }

            // Timeout errors - always retry
            if (
                error.code === "ECONNABORTED" ||
                error.message?.includes("timeout")
            ) {
                return true;
            }

            // Network errors - always retry
            if (
                !error.response ||
                error.code === "ECONNRESET" ||
                error.code === "ETIMEDOUT" ||
                error.code === "ENOTFOUND" ||
                error.code === "ECONNREFUSED"
            ) {
                return true;
            }

            // Don't retry on 4xx client errors
            return false;
        };

        // Retry logic with exponential backoff
        const attemptRequest = async (attemptNumber = 1) => {
            try {
                return await makeRequest();
            } catch (error) {
                const isLastAttempt = attemptNumber >= maxRetries;
                const canRetry = shouldRetryError(error);

                if (!canRetry || isLastAttempt) {
                    throw error;
                }

                // Exponential backoff (1s, 2s, 4s, 8s, capped at 10s)
                const delayMs = Math.min(
                    1000 * Math.pow(2, attemptNumber - 1),
                    10000
                );
                await new Promise((resolve) => setTimeout(resolve, delayMs));

                return attemptRequest(attemptNumber + 1);
            }
        };

        return attemptRequest();
    }

    setToken(params) {
        return new Promise((resolve) => {
            window.localStorage.setItem("login-token", params);
            resolve();
        });
    }

    removeToken() {
        return new Promise((resolve) => {
            window.localStorage.removeItem("login-token");
            resolve();
        });
    }

    getToken() {
        return window.localStorage.getItem("login-token");
    }

    /**
     * Get Facebook Pixel cookies from browser
     * Uses the FacebookTracker to get cookies with URL parameter support
     * @returns {Object} Object with fbpCookie and fbcCookie
     */
    getFacebookPixelCookies() {
        return fbTracker.getFacebookPixelCookies();
    }

    /**
     * Get UTM parameters from localStorage
     * @returns {Object} Object with utm_source, utm_medium, utm_campaign, utm_term, utm_content
     */
    getUtmParameters() {
        if (typeof window === "undefined") {
            return {
                utm_source: undefined,
                utm_medium: undefined,
                utm_campaign: undefined,
                utm_term: undefined,
                utm_content: undefined,
            };
        }

        try {
            const utmParams = localStorage.getItem("utm_params");
            if (utmParams) {
                const parsed = JSON.parse(utmParams);
                return {
                    utm_source: parsed.utm_source || undefined,
                    utm_medium: parsed.utm_medium || undefined,
                    utm_campaign: parsed.utm_campaign || undefined,
                    utm_term: parsed.utm_term || undefined,
                    utm_content: parsed.utm_content || undefined,
                };
            }
        } catch (error) {
            console.log(
                "Error reading UTM parameters from localStorage:",
                error
            );
        }

        return {
            utm_source: undefined,
            utm_medium: undefined,
            utm_campaign: undefined,
            utm_term: undefined,
            utm_content: undefined,
        };
    }

    getStripePublishableKey() {
        return this.stripePublishableKey;
    }

    getProxyImageUrl() {
        return this.proxyImageUrl;
    }

    signup(params) {
        // Include Facebook Pixel cookies and UTM parameters in signup request
        const facebookCookies = this.getFacebookPixelCookies();
        const utmParams = this.getUtmParameters();
        return this.call("post", "users/login/signup", {
            ...params,
            ...facebookCookies,
            ...utmParams,
        });
    }

    login(params) {
        // withCredentials so the browser stores Set-Cookie from the login
        // response (OAuth-callback session cookie).
        return this.call("post", "users/login", params, { withCredentials: true });
    }

    authenticateWithMagicLink(params) {
        return this.call("post", "users/login/magic-link", params, {
            withCredentials: true,
        });
    }

    userRequestCode(params) {
        return this.call("get", "users/login/request-code", params);
    }

    checkSSOByEmail(params) {
        return this.call("get", "users/sso/check", params);
    }

    ssoLoginUser(params) {
        return this.call("get", "users/sso/login", params);
    }

    user(params) {
        return this.call("get", "users/profile", params, {
            retry: true,
            retryCount: 3,
        });
    }

    updateUser(params) {
        // Include UTM parameters if they exist in localStorage and aren't already provided
        const utmParams = this.getUtmParameters();
        const hasUtmInParams =
            params.utm_source ||
            params.utm_medium ||
            params.utm_campaign ||
            params.utm_term ||
            params.utm_content;

        // Only include UTM params if they're not already in the request and at least one exists
        const shouldIncludeUtm =
            !hasUtmInParams &&
            (utmParams.utm_source ||
                utmParams.utm_medium ||
                utmParams.utm_campaign ||
                utmParams.utm_term ||
                utmParams.utm_content);

        return this.call("put", "users/profile", {
            ...params,
            ...(shouldIncludeUtm ? utmParams : {}),
        });
    }

    logout(params) {
        // withCredentials so the Set-Cookie: Max-Age=0 from the logout
        // response is respected by the browser (cookie invalidation).
        return this.call("post", "users/logout", params, { withCredentials: true });
    }

    reportError(params) {
        return this.call("post", "emails/error-report", params);
    }

    switchCompany(params) {
        return this.call("put", "users/switch-company", params);
    }

    createCompany(params) {
        return this.call("post", "users/create-company", params);
    }

    getCompanyLogo(params) {
        return this.call("get", "company/logo", params);
    }

    updateCompanyLogo(params) {
        return this.call("put", "company/logo", params);
    }

    signedUrl(params) {
        return this.call("get", "files/upload-url", params);
    }

    insertFile(params) {
        return this.call("post", "files", params);
    }

    transferFileToPublic(params) {
        return this.call("put", `files/${params.fileId}/public`, params);
    }

    getUsers(params) {
        return this.call("get", "users", params);
    }

    createUser(params) {
        return this.call("post", "users/create", params);
    }

    deleteUser(params) {
        return this.call("delete", "users/delete", params);
    }

    updateUserRole(params) {
        return this.call("put", "users/update-role", params);
    }

    // App methods
    createApp(params) {
        return this.call("post", "apps", params);
    }

    createWorkflow(params) {
        // Include Facebook Pixel cookies in workflow creation request
        const facebookCookies = this.getFacebookPixelCookies();
        return this.call("post", "workflows", {
            ...params,
            ...facebookCookies,
        });
    }

    workflowsList(params) {
        return this.call("get", "workflows", params);
    }

    recentWorkflows(params) {
        return this.call("get", "workflows/recent", params);
    }

    workflowCount(params) {
        return this.call("get", "workflows/count", params);
    }

    toggleArchiveWorkflow(params) {
        return this.call("put", "workflows/archive", params);
    }

    duplicateWorkflow(params) {
        return this.call("post", "workflows/duplicate", params);
    }

    bulkArchiveWorkflows(params) {
        return this.call("put", "workflows/bulk-archive", params);
    }

    moveWorkflowsToApp(params) {
        return this.call("put", "workflows/move-to-app", params);
    }

    getGroupedWorkflows(params) {
        return this.call("get", "workflows/grouped", params);
    }

    getClientWorkflowByInternalDomain(params) {
        return this.call(
            "get",
            `workflows/by-internal-domain/${params.internalDomain}`,
            params
        );
    }

    takeWorkflowScreenshot(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/screenshot`,
            params
        );
    }

    appList(params) {
        return this.call("get", "apps", params);
    }

    app(params) {
        return this.call("get", `apps/${params?.id}`, params);
    }

    appDelete(params) {
        return this.call("delete", "apps", params);
    }

    toggleArchiveApp(params) {
        return this.call("put", "apps/archive", params);
    }

    appUpdate(params) {
        return this.call("put", "apps", params);
    }

    togglePublishApp(params) {
        return this.call("put", "apps/publish", params);
    }

    generateAppApiKey(params) {
        return this.call("post", "apps/generate-keys", params);
    }

    getAppFromApiKey(params) {
        return this.call("get", `apps/api-key/${params?.apiKey}`, params);
    }

    insertChat(params) {
        return this.call("post", "chats", params);
    }

    insertChatMessage(params) {
        return this.call("post", "chats/messages", params);
    }

    getChatHistory(params) {
        return this.call("get", `chats/${params?.chatPublicId}`, params);
    }

    sendContactForm(params) {
        return this.call("post", "emails/contact", params);
    }

    getAppChats(params) {
        return this.call("get", `chats/app/${params?.appId}`, params);
    }

    integrationsList(params) {
        return this.call("get", "integrations", params);
    }

    // Single-integration fetch with credentials redacted. Pair with
    // integrationsList — the list call never ships `config`, so any
    // surface that needs to populate an edit form (e.g. the
    // NewIntegrationModal) must call this with the row id. Sensitive
    // keys (password, apiKey, OAuth/refresh tokens, encrypted blobs)
    // come back as `null` when set, so the modal can show a
    // "leave unchanged" placeholder instead of cleartext.
    //
    // Path is `integrations/details/:id` (NOT `integrations/:id`) on
    // purpose — the bare `:id` segment would collide with literal
    // routes under /integrations/ (community, api-key, etc.). The
    // two-segment form can never shadow a literal.
    getIntegration(params) {
        return this.call(
            "get",
            `integrations/details/${params.integrationId}`,
            params
        );
    }

    createIntegration(params) {
        return this.call("post", "integrations", params);
    }

    getIntegrationAlerts(params) {
        return this.call("get", "integrations/alerts", params);
    }

    getIntegrationAlertCount(params) {
        return this.call("get", "integrations/alerts/count", params);
    }

    dismissIntegrationAlert(params) {
        return this.call("post", `integrations/alerts/${params.alertId}/dismiss`, params);
    }

    createFunctionGroup(params) {
        return this.call("post", "function-groups", params);
    }

    getFunctionGroupsList(params) {
        return this.call("get", "function-groups", params);
    }

    getFunctionGroup(params) {
        return this.call("get", `function-groups/${params?.id}`, params);
    }

    insertFunction(params) {
        return this.call(
            "post",
            `function-groups/${params.functionGroupId}/functions`,
            params
        );
    }

    deleteFunction(params) {
        return this.call(
            "delete",
            `function-groups/functions/${params.functionId}`,
            params
        );
    }

    updateFunction(params) {
        return this.call(
            "put",
            `function-groups/functions/${params.functionId}`,
            params
        );
    }

    toggleArchiveFunctionGroup(params) {
        return this.call(
            "put",
            `function-groups/archive/${params.functionGroupId}`,
            params
        );
    }

    updateFunctionGroup(params) {
        return this.call("put", `function-groups/${params.id}`, params);
    }

    // Waitlist methods
    joinWaitlist(params) {
        return this.call("post", "waitlist", params);
    }

    getWaitlist(params) {
        return this.call("get", "waitlist", params);
    }

    updateWaitlistStatus(params) {
        return this.call("put", `waitlist/${params.entryId}`, params);
    }

    executeFunction(params) {
        return this.call(
            "post",
            `function-groups/execute/${params.functionId}`,
            params
        );
    }

    generateQAFromText(params, options = {}) {
        return this.call("post", "chats/generate-qa", params, {
            ...options,
            timeout: 100000,
        });
    }

    scrapeWebsite(params, options = {}) {
        return this.call("post", "chats/scrape-website", params, {
            ...options,
            timeout: 100000,
        });
    }

    createWorkflowStep(params) {
        return this.call("post", "workflows/steps", params);
    }

    workflow(params) {
        return this.call("get", `workflows/${params?.id}`, params, {
            retry: true,
            retryCount: 3,
            timeout: 30000,
        });
    }

    deleteWorkflowStep(params) {
        return this.call("delete", `workflows/steps/${params.stepId}`, params);
    }

    updateWorkflowStep(params) {
        return this.call("put", `workflows/steps/${params.stepId}`, params);
    }

    updateWorkflow(params) {
        return this.call("put", `workflows/${params.id}`, params);
    }

    updateSSOConfig(params) {
        return this.call("post", "company/sso/config", params);
    }

    getSSOConfig(params) {
        return this.call("get", "company/sso/config", params);
    }

    initiateDomainVerification(params) {
        return this.call("post", "company/sso/domain-verify", params);
    }

    checkDomainVerification(params) {
        return this.call("post", "company/sso/domain-verify-check", params);
    }

    removeSSODomain(params) {
        return this.call("post", "company/sso/domain-remove", params);
    }

    testSSOConfig(params) {
        return this.call("get", "company/sso/test", params);
    }

    approveDomainVerification(params) {
        return this.call("post", "company/sso/domain-approve", params);
    }

    updateDesignSystem(params) {
        return this.call("post", "company/design-system", params);
    }

    getDesignSystem(params) {
        return this.call("get", "company/design-system", params);
    }

    analyzeDesignFromImage(params) {
        return this.call(
            "post",
            "company/design-system/analyze-image",
            params,
            {
                timeout: 60000,
            }
        );
    }

    analyzeLogo(params) {
        return this.call("post", "company/design-system/analyze-logo", params, {
            timeout: 60000,
        });
    }

    generateWorkflowServiceToken(params) {
        return this.call(
            "post",
            `workflows/${params.id}/service-token`,
            params
        );
    }

    // Workflow User Management
    generateWorkflowUserToken(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/auth/admin/generate-user-token`,
            params
        );
    }

    issueWorkflowPreviewToken(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/auth/preview-token`,
            params
        );
    }

    getWorkflowUsers(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/auth/users`,
            params
        );
    }

    createWorkflowUser(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/auth/users`,
            params
        );
    }

    updateWorkflowUser(params) {
        return this.call(
            "put",
            `workflows/${params.workflowId}/auth/users/${params.workflowUserId}`,
            params
        );
    }

    deleteWorkflowUser(params) {
        return this.call(
            "delete",
            `workflows/${params.workflowId}/auth/users/${params.workflowUserId}`,
            params
        );
    }

    getWorkflowUserEntitySchema(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/auth/user-entity-schema`,
            params
        );
    }

    startWorkflow(params, options = {}) {
        return this.call("post", `workflows/${params.id}/start`, params, {
            ...options,
            timeout: 1000000,
        });
    }

    cancelWorkflowExecution(params) {
        return this.call(
            "post",
            `workflows/executions/${params.executionId}/cancel`,
            params
        );
    }

    startWorkflowWithPublicKey(params, options = {}) {
        return this.call(
            "post",
            `webhook/pdt-private-key/${params.publicKey}`,
            params,
            {
                ...options,
                timeout: 1000000,
            }
        );
    }

    saveWorkflowTestCase(params) {
        return this.call("post", "workflows/test-cases", params);
    }

    getWorkflowTestCases(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/test-cases`,
            params
        );
    }

    runWorkflowTests(params) {
        return this.call("post", "workflows/run-tests", params);
    }

    getTestRuns(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/test-runs`,
            params
        );
    }

    getTestRun(params) {
        return this.call("get", `workflows/test-runs/${params.id}`, params);
    }

    updateWorkflowTestCase(params) {
        return this.call("put", `workflows/test-cases/${params.id}`, params);
    }

    deleteWorkflowTestCase(params) {
        return this.call("delete", `workflows/test-cases/${params.id}`, params);
    }

    applyTestFix(params) {
        return this.call("post", "workflows/apply-test-fix", params);
    }

    generateComponent(params) {
        return this.call("post", "components/generate", params, {
            timeout: 60000, // Increased timeout for AI component generation
        });
    }

    getGeneratedComponent(functionId) {
        return this.call("get", `components/${functionId}`);
    }

    getGeneratedComponentHistoricalGeneration(params) {
        return this.call(
            "get",
            `components/historical/${params.functionId}`,
            params
        );
    }

    chatWithWorkflow(params) {
        return this.call("post", "langchain/workflows", params, {
            timeout: 100000000000,
        });
    }

    getDropboxAuthUrl(params) {
        return this.call("get", "integrations/dropbox/auth-url", params);
    }

    deleteIntegration(params) {
        return this.call(
            "delete",
            `integrations/${params.integrationId}`,
            params
        );
    }

    getWorkflowDatabases(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/databases`,
            params
        );
    }

    addFileToWorkflow(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/files`,
            params
        );
    }

    removeFileFromWorkflow(params) {
        return this.call(
            "delete",
            `workflows/${params.workflowId}/files`,
            params
        );
    }

    extractExcel(params) {
        return this.call("get", "documents/extract-excel", params);
    }

    extractCsv(params) {
        return this.call("get", "documents/extract-csv", params);
    }

    createPrompt(params) {
        return this.call("post", "prompts", params);
    }

    getPrompts(params) {
        return this.call("get", "prompts", params);
    }

    updatePrompt(params) {
        return this.call("put", "prompts", params);
    }

    updateIntegration(params) {
        return this.call("put", `integrations/${params.integrationId}`, params);
    }

    getAllWorkflowIntegrations(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/integrations`,
            params
        );
    }

    updateIntegrationSecurity(params) {
        return this.call(
            "put",
            `workflows/${params.workflowId}/integrations/${params.integrationId}/security`,
            params
        );
    }

    getTaskStatus(params) {
        return this.call("get", `background-tasks/${params.taskId}`, params, {
            retry: true,
            retryCount: 2,
        });
    }

    startIntegrationBuild(params) {
        return this.call("post", "langchain/build-integration", params);
    }

    testIntegrationEndpoint(params) {
        return this.call("post", "integrations/test-endpoint", params);
    }

    getCommunityIntegrations() {
        return this.call("get", "integrations/community");
    }

    saveDraftIntegration(params) {
        return this.call("post", "integrations/community/draft", params);
    }

    deleteCommunityIntegration(params) {
        return this.call("post", "integrations/community/delete", params);
    }

    publishCommunityIntegration(params) {
        return this.call("post", "integrations/community/publish", params);
    }

    updateCommunityIntegration(params) {
        return this.call("put", "integrations/community/update", params);
    }

    connectCommunityIntegration(params) {
        return this.call("post", "integrations/community/connect", params);
    }

    disconnectCommunityIntegration(params) {
        return this.call("post", "integrations/community/disconnect", params);
    }

    fixIntegrationEndpoint(params) {
        return this.call("post", "langchain/fix-integration-endpoint", params);
    }

    chatWithIntegrationAgent(params) {
        return this.call("post", "langchain/fix-integration-endpoint", params);
    }

    /**
     * Fetch a short-lived one-time ticket for secure WebSocket auth.
     * Falls back to null if the endpoint is unavailable (old server).
     *
     * Response shape: axios `res.data` is the HTTP body, which is
     * `{ status, success, data: { ticket } }` — so the ticket lives
     * at `res.data.data.ticket`. Previously this looked at
     * `res.data.ticket` and silently returned null on every call.
     * @returns {Promise<string|null>}
     */
    async fetchWsTicket() {
        try {
            const res = await this.call("post", "ws/ticket", {});
            const ticket =
                res?.data?.data?.ticket ?? res?.data?.ticket ?? null;
            return ticket || null;
        } catch {
            return null;
        }
    }

    /**
     * Open a WebSocket connection for real-time task status updates.
     * Uses ticket-based auth (preferred) with JWT fallback.
     * Returns a Promise that resolves to a WebSocket or null.
     * @param {string} taskId - The task public key to subscribe to
     * @param {function} onMessage - Callback for incoming messages
     * @returns {Promise<WebSocket|null>}
     */
    async connectTaskWebSocket(taskId, onMessage) {
        const token = this.getToken();
        if (!token || typeof WebSocket === "undefined") return null;

        const wsProtocol = this.apiUrl.startsWith("https") ? "wss" : "ws";
        const wsBase = this.apiUrl.replace(/^https?/, wsProtocol);

        // Try ticket-based auth first, fall back to token in URL
        const ticket = await this.fetchWsTicket();
        const authParam = ticket
            ? `ticket=${encodeURIComponent(ticket)}`
            : `token=${encodeURIComponent(token)}`;
        const wsUrl = `${wsBase}/ws/tasks?${authParam}`;

        try {
            const ws = new WebSocket(wsUrl);
            ws.onopen = () => {
                ws.send(JSON.stringify({ type: "subscribe", taskId }));
            };
            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    onMessage(data);
                } catch (e) {
                    // Ignore parse errors
                }
            };
            return ws;
        } catch (err) {
            return null;
        }
    }

    getRunningTasksForWorkflow(params) {
        return this.call(
            "get",
            `background-tasks/workflow/${params.workflowId}/running`,
            params
        );
    }

    getGoogleAuthUrl(params) {
        return this.call("get", "integrations/google/auth-url", params);
    }

    getGoogleAuthUrlForLogin(params) {
        return this.call("get", "auth/google/url", params);
    }

    updateWorkflowTrigger(params) {
        return this.call(
            "put",
            `workflows/${params.workflowId}/trigger`,
            params
        );
    }

    deleteWorkflowTrigger(params) {
        return this.call(
            "delete",
            `workflows/${params.workflowId}/trigger/${params.triggerId}`,
            params
        );
    }

    getDatabases(params) {
        return this.call("get", "databases", params);
    }

    cloneDatabase(params) {
        return this.call("post", "databases/clone", params);
    }

    getTableRecords(params) {
        return this.call("post", "databases/records/query", params);
    }

    acceptTerms(params) {
        return this.call("post", "users/accept-terms", params);
    }

    saveTableRecord(params) {
        return this.call("post", "databases/logged/records", params);
    }

    deleteManyRecord(params) {
        return this.call("delete", `databases/logged/records/`, params);
    }

    insertNewStripeSession(params) {
        return this.call("post", "stripe/session", params);
    }

    createSubscriptionCheckout(params) {
        return this.call("post", "stripe/checkout", params);
    }

    cancelSubscription(params) {
        return this.call("post", "stripe/cancel", params);
    }

    pauseSubscription(params) {
        return this.call("post", "stripe/pause", params);
    }

    sendSupportMessage(params) {
        return this.call("post", "emails/support", params);
    }

    getPieces(params) {
        return this.call("get", "pieces", params);
    }

    getPieceByName(params) {
        return this.call("get", `pieces/${params.pieceName}`, params);
    }

    generateCodeContext(params) {
        return this.call("post", "langchain/generate-code-context", params);
    }

    breakdownUserRequirement(params) {
        return this.call(
            "post",
            "langchain/breakdown-user-requirement",
            params
        );
    }

    getAvailableWhatsappNumbers(params) {
        return this.call("get", "integrations/whatsapp/numbers", params);
    }

    // Tutorial methods
    markTutorialComplete(params) {
        return this.call("post", "users/tutorials/complete", params);
    }

    analyzeGoal(params) {
        return this.call("post", "langchain/analyze-goal", params);
    }

    analyzeRequirements(params) {
        return this.call("post", "langchain/analyze-requirements", params);
    }

    generateCodeFromInstructions(params) {
        return this.call("post", "langchain/generate-code", params);
    }

    generateStepContext(params) {
        return this.call("post", "langchain/generate-step-context", params);
    }

    createCreditCheckout(params) {
        return this.call("post", "stripe/credit-checkout", params);
    }

    getStepChatMessages(params) {
        return this.call("get", "step-chat-messages", params);
    }

    insertStepChatMessage(params) {
        return this.call("post", "step-chat-messages", params);
    }

    archiveStepChat(params) {
        return this.call("put", "step-chat-messages/archive", params);
    }

    rollbackStepChat(params) {
        return this.call("post", "step-chat-messages/rollback", params);
    }

    getWorkflowExecutionById(params) {
        return this.call(
            "get",
            `workflows/executions/${params.executionId}`,
            params
        );
    }

    getWorkflowStepVersion(params) {
        return this.call(
            "get",
            `workflows/steps/${params.stepId}/versions/${params.versionId}`,
            params
        );
    }

    getVersionDiff(params) {
        return this.call(
            "get",
            `workflows/steps/${params.stepId}/versions/${params.versionId}/diff`,
            params
        );
    }

    getWorkflowStepVersions(params) {
        return this.call(
            "get",
            `workflows/steps/${params.stepId}/versions`,
            params
        );
    }

    rollbackWorkflowStep(params) {
        return this.call(
            "post",
            `workflows/steps/${params.stepId}/rollback`,
            params
        );
    }

    getAllWorkflowExecutions(params) {
        return this.call("get", "workflows/executions", params);
    }

    getWorkflowNames(params) {
        return this.call("get", "workflows/names", params);
    }

    getShopifyAuthUrl(params) {
        return this.call("get", "integrations/shopify/auth-url", params);
    }

    shopifyAuthHandler(params) {
        return this.call("get", "users/shopify/auth", params);
    }

    getMicrosoftAuthUrl(params) {
        return this.call("get", "integrations/microsoft/auth-url", params);
    }

    getMicrosoftAuthUrlForLogin(params) {
        return this.call("get", "auth/microsoft/url", params);
    }

    getUserFavorites(params) {
        return this.call("get", "users/favorites", params);
    }

    addUserFavorite(params) {
        return this.call("post", "users/favorites", params);
    }

    removeUserFavorite(params) {
        return this.call("delete", "users/favorites", params);
    }

    getEnphaseAuthUrl(params) {
        return this.call("get", "integrations/enphase/auth-url", params);
    }

    getGoogleCalendarAuthUrl(params) {
        return this.call("get", "integrations/google-calendar/auth-url", params);
    }

    insertNewBrowserSession(params) {
        return this.call("post", "browser/sessions", params);
    }

    performBrowserAction(params) {
        return this.call("post", "browser/actions", params);
    }

    checkTriggerData(params) {
        return this.call("get", `workflows/${params.id}/trigger-data`, params);
    }

    testSQLConnection(params) {
        return this.call("post", "integrations/sql/test-connection", params);
    }

    testOdooConnection(params) {
        return this.call("post", "integrations/odoo/test-connection", params);
    }

    getOdooModules(params) {
        return this.call("post", "integrations/odoo/installed-modules", params);
    }

    createSubscriptionWithTrial(params) {
        // Include Facebook Pixel cookies in subscription trial request (creates user)
        const facebookCookies = this.getFacebookPixelCookies();
        return this.call("post", "stripe/subscription-trial", {
            ...params,
            ...facebookCookies,
        });
    }

    addTableColumn(params) {
        return this.call("post", "databases/columns", params);
    }

    deleteTableColumn(params) {
        return this.call("delete", "databases/columns", params);
    }

    updateTableColumn(params) {
        return this.call("put", "databases/columns", params);
    }

    createWorkflowTableAccess(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/databases/tables/access`,
            params
        );
    }

    getWorkflowTableAccess(params) {
        // Handle different query patterns
        if (params.workflowId && params.tableId) {
            // Get specific access
            return this.call(
                "get",
                `workflows/${params.workflowId}/databases/tables/${params.tableId}/access`,
                params
            );
        } else if (params.workflowId) {
            // Get all accesses for a workflow
            return this.call(
                "get",
                `workflows/${params.workflowId}/databases/tables/access`,
                params
            );
        } else {
            // Get all accesses
            return this.call("get", "databases/tables/access", params);
        }
    }

    deleteWorkflowTableAccess(params) {
        return this.call(
            "delete",
            `workflows/${params.workflowId}/databases/tables/${params.tableId}/access`,
            params
        );
    }

    // Database Access Grants (cross-app sharing)
    getDatabaseAccessGrants(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/databases/grants`,
            params
        );
    }

    createDatabaseAccessGrant(params) {
        return this.call("post", "databases/grants", params);
    }

    deleteDatabaseAccessGrant(params) {
        return this.call(
            "delete",
            `databases/grants/${params.grantId}`,
            params
        );
    }

    checkDatabaseAccessGrant(params) {
        return this.call("get", `databases/grants/check`, params);
    }

    requestDatabaseAccess(params) {
        return this.call("post", "databases/grants/request", params);
    }

    // Function Grants (cross-app function sharing)
    getShareableFunctions(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/shareable-functions`,
            params
        );
    }

    getFunctionGrants(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/function-grants`,
            params
        );
    }

    createFunctionGrant(params) {
        return this.call("post", "function-grants", params);
    }

    deleteFunctionGrant(params) {
        return this.call("delete", `function-grants/${params.grantId}`, params);
    }

    getCompanyFunctions(params) {
        return this.call("get", "company-functions", params);
    }

    setupSharedFunctionApiAccess(params) {
        return this.call("post", "function-api-access", params);
    }

    getCompanyWorkflows(params) {
        return this.call("get", "workflows/company", params);
    }

    getCompanyWorkflowsWithPages(params) {
        return this.call("get", "workflows/company/pages", params);
    }

    getWorkflowEntitySchema(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/entity/schema`,
            params
        );
    }

    deleteFile(params) {
        return this.call("delete", `files/${params.fileId}`, params);
    }

    getFileSignedUrl(params) {
        return this.call("get", `files/${params.fileId}/signed-url`, params);
    }

    getSessionDetails(params) {
        // The framework injects req.sessionId into the handler's flat args and
        // overrides any query-string `sessionId` with the caller's auth
        // session id. Forward under a non-colliding name so the Stripe
        // checkout session id actually reaches the handler.
        const { sessionId, stripeSessionId, ...rest } = params || {};
        return this.call("get", "stripe/session-details", {
            ...rest,
            stripeSessionId: stripeSessionId ?? sessionId,
        });
    }

    // Payment Methods management
    getPaymentMethods(params) {
        return this.call("get", "stripe/payment-methods", params);
    }

    getInvoices(params) {
        return this.call("get", "stripe/invoices", params);
    }

    addPaymentMethod(params) {
        return this.call("post", "stripe/payment-method", params);
    }

    removePaymentMethod(params) {
        return this.call("delete", "stripe/payment-method", params);
    }

    setDefaultPaymentMethod(params) {
        return this.call("put", "stripe/payment-method/default", params);
    }

    // Blueprint methods
    createBlueprint(params) {
        return this.call("post", "blueprints", params);
    }

    getBlueprints(params) {
        return this.call("get", "blueprints", params);
    }

    getBlueprintById(params) {
        return this.call("get", `blueprints/${params.id}`, params);
    }

    createWorkflowFromBlueprint(params) {
        return this.call("post", "blueprints/create-workflow", params);
    }

    recommendBlueprints(params) {
        return this.call("get", "blueprints/recommend", params);
    }

    aiRecommendBlueprint(params) {
        return this.call("post", "blueprints/ai-recommend", params);
    }

    getBlueprintRequests(params) {
        return this.call("get", "blueprints/requests", params);
    }

    updateBlueprintRequest(params) {
        return this.call("put", `blueprints/requests/${params.id}`, params);
    }

    triggerBlueprintCategorization(params) {
        return this.call("post", "blueprints/categorize", params);
    }

    getBlueprintAnalytics(params) {
        return this.call("get", "blueprints/analytics", params);
    }

    updateBlueprintBadges(params) {
        return this.call(
            "put",
            `blueprints/${params.blueprintId}/badges`,
            params
        );
    }

    publishBlueprint(params) {
        return this.call("put", `blueprints/${params.id}/publish`, params);
    }

    getBlueprintByShareToken(params) {
        return this.call(
            "get",
            `blueprints/share/${params.shareToken}`,
            params
        );
    }

    subscribeToBlueprint(params) {
        return this.call("post", `blueprints/${params.id}/subscribe`, params);
    }

    getMySubscriptions(params) {
        return this.call("get", "subscriptions/mine", params);
    }

    checkSubscriptionUpdate(params) {
        return this.call(
            "get",
            `subscriptions/${params.id}/check-update`,
            params
        );
    }

    applySubscriptionUpdate(params) {
        return this.call(
            "post",
            `subscriptions/${params.id}/apply-update`,
            params
        );
    }

    updateSubscriptionPolicy(params) {
        return this.call("patch", `subscriptions/${params.id}/policy`, params);
    }

    ejectSubscription(params) {
        return this.call("post", `subscriptions/${params.id}/eject`, params);
    }

    getBlueprintSubscribers(params) {
        return this.call("get", `blueprints/${params.id}/subscribers`, params);
    }

    getMaintainableBlueprintByWorkflow(params) {
        return this.call(
            "get",
            `blueprints/by-workflow/${params.workflowId}`,
            params
        );
    }

    startCodeGeneration(params) {
        // Include Facebook Pixel cookies in code generation request
        const facebookCookies = this.getFacebookPixelCookies();
        return this.call(
            "post",
            "langchain/start-code-generation",
            {
                ...params,
                ...facebookCookies,
            },
            {
                timeout: 300000000,
            }
        );
    }

    cancelCodeGeneration(params) {
        return this.call("post", "langchain/cancel-code-generation", params, {
            timeout: 30000,
        });
    }

    createDatabase(params) {
        return this.call("post", "databases", params);
    }

    createTable(params) {
        return this.call("post", "databases/table", params);
    }

    deleteDatabase(params) {
        return this.call("delete", `databases/${params.databaseId}`, params);
    }

    deleteTable(params) {
        return this.call(
            "delete",
            `databases/tables/${params.tableId}`,
            params
        );
    }

    getWhatsAppTemplates(params) {
        return this.call("get", "integrations/whatsapp/templates", params);
    }

    createWhatsAppTemplate(params) {
        return this.call("post", "integrations/whatsapp/templates", params);
    }

    getWhatsAppTemplateStatus(params) {
        return this.call(
            "get",
            "integrations/whatsapp/template-status",
            params
        );
    }

    deleteWhatsAppTemplate(params) {
        return this.call("delete", "integrations/whatsapp/templates", params);
    }

    compactChatConversation(params) {
        return this.call(
            "post",
            "langchain/compact-chat-conversation",
            params,
            {
                timeout: 100000000000,
            }
        );
    }

    getFilesList(params) {
        return this.call("get", "files", params);
    }

    getFileWorkflows(params) {
        return this.call("get", "files/workflows", params);
    }

    // Groups methods
    getGroups(params) {
        return this.call("get", "groups", params);
    }

    getGroupMembers(params) {
        return this.call("get", `groups/${params.groupId}/members`, params);
    }

    createGroup(params) {
        return this.call("post", "groups", params);
    }

    updateGroup(params) {
        return this.call("put", `groups/${params.id}`, params);
    }

    deleteGroup(params) {
        return this.call("delete", `groups/${params.id}`, params);
    }

    addMemberToGroup(params) {
        return this.call("post", `groups/${params.groupId}/members`, params);
    }

    removeMemberFromGroup(params) {
        return this.call(
            "delete",
            `groups/${params.groupId}/members/${params.memberId}`,
            params
        );
    }

    publishToGroups(params) {
        return this.call("post", "apps/publish-to-groups", params);
    }

    unpublishToGroups(params) {
        return this.call("delete", "apps/unpublish-from-groups", params);
    }

    getAppPublicationInfo(params) {
        return this.call(
            "get",
            `apps/publication-info/${params.appId}`,
            params
        );
    }

    getAuditLogs(params) {
        return this.call("get", "audit-logs", params);
    }

    logExportAudit(params) {
        return this.call("post", "audit/export", params);
    }

    getFunctionInvocations(params) {
        return this.call("get", "function-invocations", params);
    }

    getFunctionInvocationById(params) {
        return this.call("get", "function-invocations/by-id", params);
    }

    getFunctionInvocationStats(params) {
        return this.call("get", "function-invocations/stats", params);
    }

    getFunctionInvocationTrends(params) {
        return this.call("get", "function-invocations/trends", params);
    }

    getComponentsByStepId(params) {
        return this.call(
            "get",
            `workflow-steps/${params.stepId}/components`,
            params
        );
    }

    updateComponent(params) {
        return this.call("put", `components/${params.id}`, params);
    }

    createComponent(params) {
        return this.call(
            "post",
            `workflow-steps/${params.stepId}/components`,
            params
        );
    }

    deleteComponent(params) {
        // Support deletion by id or filePath
        const url = params.id
            ? `components/${params.id}`
            : "components/by-filepath";
        return this.call("delete", url, params);
    }

    getCompanyAgentUICredit(params) {
        return this.call("get", "credits/agentui", params);
    }

    getAutoChargeSettings(params) {
        return this.call("get", "credits/auto-charge", params);
    }

    updateAutoChargeSettings(params) {
        return this.call("put", "credits/auto-charge", params);
    }

    purchaseCreditsWithCard(params) {
        return this.call("post", "credits/purchase", params);
    }

    confirmCreditPayment(params) {
        return this.call("post", "credits/confirm-payment", params);
    }

    getCreditUsageHistory(params) {
        return this.call("get", "credits/usage-history", params);
    }

    getCreditUsageChart(params) {
        return this.call("get", "credits/usage-chart", params);
    }

    getModuleCostEstimates(params) {
        return this.call("get", "credits/module-costs", params);
    }

    createSetupIntent(params) {
        return this.call("post", "stripe/create-setup-intent", params);
    }

    createCryptoPaymentIntent(params) {
        return this.call("post", "stripe/create-crypto-payment-intent", params);
    }

    trackCheckoutOpened(params) {
        return this.call("post", "stripe/track-checkout-opened", params);
    }

    updateSubscription(params) {
        return this.call("post", "stripe/update-subscription", params);
    }

    resumeSubscription(params) {
        return this.call("post", "stripe/resume", params);
    }

    // Secrets management methods
    getSecrets(params) {
        return this.call("get", "secrets", params);
    }

    upsertSecret(params) {
        return this.call("post", "secrets", params);
    }

    deleteSecret(params) {
        return this.call("delete", `secrets/${params.secretId}`, params);
    }

    associateSecretWithWorkflow(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/secrets/${params.secretId}`,
            params
        );
    }

    removeSecretFromWorkflow(params) {
        return this.call(
            "delete",
            `workflows/${params.workflowId}/secrets/${params.secretId}`,
            params
        );
    }

    removeSecretFromEnvironment(params) {
        return this.call(
            "delete",
            `secrets/${params.secretId}/environments`,
            params
        );
    }

    upsertSecretValue(params) {
        return this.call("put", `secrets/${params.secretId}/value`, params);
    }

    updateWorkflowStepVersion(params) {
        return this.call(
            "put",
            `workflows/steps/versions/${params.versionId}`,
            params
        );
    }

    getComponentHistory(params) {
        return this.call("get", `components/history/${params.id}`, {
            page: params.page,
            limit: params.limit,
        });
    }

    getComponentVersionDetail(params) {
        return this.call("get", `component-versions/${params.id}`, params);
    }

    getComponentCode(params) {
        return this.call("get", `components/${params.id}/code`, {
            environmentName: params.environmentName,
        });
    }

    getComponentCodeBulk(params) {
        return this.call("post", `components/code/bulk`, params);
    }

    getStepChatHistory(params) {
        return this.call("get", `step-chat-messages/history`, params);
    }

    getStepChatPreview(params) {
        return this.call(
            "get",
            `step-chat-messages/preview/${params.stepChatId}`,
            params
        );
    }

    setActiveStepChat(params) {
        return this.call("post", `step-chat-messages/set-active`, params);
    }

    getAllStepChats(params) {
        return this.call("get", "step-chat-messages/all", params);
    }

    getStepChatFilters(params) {
        return this.call("get", "step-chat-messages/filters", params);
    }

    getStepChatDetail(params) {
        return this.call(
            "get",
            `step-chat-messages/detail/${params.stepChatId}`,
            params
        );
    }

    exportStepChat(params) {
        return this.call(
            "get",
            `admin/chat/${params.stepChatId}/export`,
            params
        );
    }

    importStepChat(params) {
        return this.call("post", "admin/chat/import", params);
    }

    rateStepChat(params) {
        return this.call("put", "step-chat-messages/rate", params);
    }

    // Admin Workflows
    getAllAdminWorkflows(params) {
        return this.call("get", "workflows/admin/all", params);
    }

    getAdminWorkflowFilters(params) {
        return this.call("get", "workflows/admin/filters", params);
    }

    // Workflow Environment Management
    getWorkflowEnvironments(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/environments`,
            params
        );
    }

    createWorkflowEnvironment(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/environments`,
            params
        );
    }

    updateWorkflowEnvironment(params) {
        return this.call(
            "put",
            `workflows/${params.workflowId}/environments/${params.environmentId}`,
            params
        );
    }

    deleteWorkflowEnvironment(params) {
        return this.call(
            "delete",
            `workflows/${params.workflowId}/environments/${params.environmentId}`,
            params
        );
    }

    setProductionLock(params) {
        return this.call(
            "put",
            `workflows/${params.workflowId}/production-lock`,
            params
        );
    }

    gitHubPush(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/github/push`,
            params
        );
    }

    gitHubPull(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/github/pull`,
            params
        );
    }

    getGitHubAuthUrl(params) {
        return this.call("get", "workflows/github/auth-url", params);
    }

    getGitHubRepos(params) {
        return this.call("get", `workflows/${params.workflowId}/github/repos`, params);
    }

    promoteToEnvironment(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/environments/${params.targetEnvironmentId}/promote`,
            params
        );
    }

    copyProductionDatabaseToEnvironment(params) {
        return this.call(
            "post",
            `workflows/${params.workflowId}/environments/${params.targetEnvironmentId}/copy-production-data`,
            params
        );
    }

    getEnvironmentDetails(params) {
        return this.call(
            "get",
            `workflows/${params.workflowId}/environments/${params.environmentId}`,
            params
        );
    }

    getReferralInfo(params) {
        return this.call("get", "users/referral", params);
    }

    getAuditFilters(params) {
        return this.call("get", "audit-filters", params);
    }

    // Support methods
    getSupportAccess(params) {
        return this.call("get", "support/access", params, {
            retry: true,
            retryCount: 3,
        });
    }

    getSupportPlans(params) {
        return this.call("get", "support/plans", params);
    }

    getSupportSubscription(params) {
        return this.call("get", "support/subscription", params);
    }

    createSupportCheckout(params) {
        return this.call("post", "support/checkout", params);
    }

    createSupportSubscription(params) {
        return this.call("post", "support/subscription", params);
    }

    cancelSupportSubscription(params) {
        return this.call("post", "support/subscription/cancel", params);
    }

    pauseSupportSubscription(params) {
        return this.call("post", "support/subscription/pause", params);
    }

    resumeSupportSubscription(params) {
        return this.call("post", "support/subscription/resume", params);
    }

    getTickets(params) {
        return this.call("get", "support/tickets", params);
    }

    getAllTickets(params) {
        return this.call("get", "support/admin/tickets", params);
    }

    // Admin support methods
    getAdminTickets(params) {
        return this.call("get", "support/admin/tickets", params);
    }

    getAdminTicketFilters() {
        return this.call("get", "support/admin/tickets/filters");
    }

    getAdminTicket(params) {
        return this.call("get", `support/admin/tickets/${params.id}`, params);
    }

    addAdminTicketReply(params) {
        return this.call(
            "post",
            `support/admin/tickets/${params.id}/reply`,
            params
        );
    }

    updateAdminTicket(params) {
        return this.call("patch", `support/admin/tickets/${params.id}`, params);
    }

    getTicket(params) {
        return this.call("get", `support/tickets/${params.id}`, params);
    }

    getWorkflowSupportTicket(params) {
        return this.call(
            "get",
            `support/workflows/${params.workflowId}/ticket`,
            params
        );
    }

    createTicket(params) {
        return this.call("post", "support/tickets", params);
    }

    addTicketReply(params) {
        return this.call(
            "post",
            `support/tickets/${params.id}/messages`,
            params
        );
    }

    updateTicketStatus(params) {
        return this.call(
            "patch",
            `support/tickets/${params.id}/status`,
            params
        );
    }

    bulkArchiveTickets(params) {
        return this.call("put", "support/admin/tickets/bulk-archive", params);
    }

    generateTicketReply(params) {
        return this.call(
            "post",
            `support/admin/tickets/${params.id}/generate-reply`,
            params,
            { timeout: 100000000000 }
        );
    }

    getDocGaps(params) {
        return this.call("get", "support/admin/doc-gaps", params);
    }

    updateDocGap(params) {
        return this.call(
            "patch",
            `support/admin/doc-gaps/${params.id}`,
            params
        );
    }

    analyzeReplyDenial(params) {
        return this.call("post", "support/admin/analyze-denial", params);
    }

    updateSupportPrompt(params) {
        return this.call("post", "support/admin/prompt", params);
    }

    getSupportPrompt(params) {
        return this.call("get", "support/admin/prompt", params);
    }

    logReplyFeedback(params) {
        return this.call("post", "support/admin/reply-feedback", params);
    }

    getAIPerformance(params) {
        return this.call("get", "support/admin/ai-performance", params);
    }

    getSupportResources() {
        return this.call("get", "support/admin/resources");
    }

    getAttachmentSignedUrl(params) {
        return this.call("get", "support/admin/attachment-signed-url", params);
    }

    testSupportAgent(params) {
        return this.call("post", "support/admin/test-agent", params, {
            timeout: 120000,
        });
    }

    getAllSupportSubscriptions(params) {
        return this.call("get", "support/subscriptions", params);
    }

    deductSupportHours(params) {
        return this.call(
            "post",
            `support/subscriptions/${params.subscriptionId}/deduct`,
            params
        );
    }

    getSupportHourLogs(params) {
        return this.call(
            "get",
            `support/subscriptions/${params.subscriptionId}/logs`,
            params
        );
    }

    // Admin Credits methods (super-admin only)
    addAdminCredits(params) {
        return this.call("post", "admin/credits/add", params);
    }

    getCompaniesForCredits(params) {
        return this.call("get", "admin/credits/companies", params);
    }

    getCompanyDetails(params) {
        return this.call("get", "admin/credits/company", params);
    }

    getCompanyCreditBreakdown(params) {
        return this.call("get", "admin/credits/company/breakdown", params);
    }

    updateCompanyName(params) {
        return this.call("patch", "admin/company/name", params);
    }

    getCompanySettings(params) {
        return this.call("get", "admin/company/settings", params);
    }

    // Admin Automation Usage (super-admin/support)
    getAutomationUsage(params) {
        return this.call("get", "admin/automation-usage", params);
    }

    setAutomationOverride(params) {
        return this.call("post", "admin/automation-usage/override", params);
    }

    resetAutomationUsageAdmin(params) {
        return this.call("post", "admin/automation-usage/reset", params);
    }

    setUsageLimitBypass(params) {
        return this.call("post", "admin/usage-limit-bypass", params);
    }

    setUserLimitBypass(params) {
        return this.call("post", "admin/company/user-limit-bypass", params);
    }

    // Facebook Pixel Audit (super-admin/support)
    getFacebookPixelAudit(params) {
        return this.call("get", "admin/facebook-audit", params);
    }

    // Operational Intelligence (super-admin/support)
    getIntelligenceErrors(params) {
        return this.call("get", "admin/intelligence/errors", params);
    }
    getIntelligenceErrorStats() {
        return this.call("get", "admin/intelligence/errors/stats");
    }
    getIntelligenceOutcomes(params) {
        return this.call("get", "admin/intelligence/outcomes", params);
    }
    getIntelligenceOutcomeStats() {
        return this.call("get", "admin/intelligence/outcomes/stats");
    }
    getIntelligenceRules(params) {
        return this.call("get", "admin/intelligence/rules", params);
    }
    updateIntelligenceRule(params) {
        return this.call("put", "admin/intelligence/rules/update", params);
    }
    getCompanyPatterns(params) {
        return this.call("get", "admin/intelligence/company-patterns", params);
    }
    updateCompanyPattern(params) {
        return this.call(
            "put",
            "admin/intelligence/company-patterns/update",
            params
        );
    }

    runAutomationPlayground(params) {
        return this.call("post", "admin/automation-agent/playground", params, {
            timeout: 300000,
        });
    }

    runAgentPlayground(params) {
        return this.call("post", "langchain/run-agent", params, {
            timeout: 120000,
        });
    }

    // Admin Data Migration (super-admin only)
    triggerCompanyBackfill(params) {
        return this.call("post", "admin/data-migration/backfill", params);
    }

    triggerAllBackfill(params) {
        return this.call("post", "admin/data-migration/backfill-all", params);
    }

    getBackfillStatus(params) {
        return this.call("get", "admin/data-migration/status", params);
    }

    requestCompanyDeletion(params) {
        return this.call("post", "admin/company/request-deletion", params);
    }

    deleteCompany(params) {
        return this.call("delete", "admin/company/delete", params);
    }

    getSecuritySummary(params) {
        return this.call("get", "security/summary", params);
    }

    getSecurityDashboard(params) {
        return this.call("get", "code-scan/security/dashboard", params);
    }

    saveSecurityTemplate(params) {
        return this.call("put", "code-scan/security/template", params);
    }

    // Code scanning methods
    triggerCodeScan(params) {
        return this.call(
            "post",
            `workflow-steps/${params.stepId}/code-scan`,
            params
        );
    }

    getCodeScanResults(params) {
        return this.call(
            "get",
            `workflow-steps/${params.stepId}/code-scan`,
            params
        );
    }

    getCodeScanHistory(params) {
        return this.call(
            "get",
            `workflow-steps/${params.stepId}/code-scan/history`,
            params
        );
    }

    getCodeScanFindings(params) {
        const query = params.status ? { status: params.status } : {};
        return this.call("get", `code-scan/${params.scanId}/findings`, query);
    }

    updateCodeScanFinding(params) {
        return this.call(
            "put",
            `code-scan/findings/${params.findingId}`,
            params
        );
    }

    getSkillRequests(params) {
        return this.call("get", "skill-requests", params);
    }

    createSkillRequest(params) {
        return this.call("post", "skill-requests", params);
    }

    voteSkillRequest(params) {
        return this.call("post", "skill-requests/vote", params);
    }

    getPublicStatus(params) {
        return this.call("get", "status", params);
    }

    // PMF Survey
    validateSurveyToken(params) {
        return this.call("get", "survey/validate", params);
    }

    submitSurveyResponse(params) {
        return this.call("post", "survey/submit", params);
    }

    getSurveyResults(params) {
        return this.call("get", "survey/results", params);
    }

    sendPmfSurveyCampaign(params) {
        return this.call("post", "survey/send-campaign", params);
    }

    sendPmfSurveyFollowupCampaign(params) {
        return this.call("post", "survey/send-followup-campaign", params);
    }

    getHarnessReports(params) {
        return this.call("get", "harness-reports", params);
    }

    getReportsByStepChat(params) {
        return this.call(
            "get",
            `harness-reports/by-chat/${params.stepChatId}`,
            params
        );
    }

    resolveHarnessReport(params) {
        return this.call("put", "harness-reports/resolve", params);
    }
}

const Bluelagoon = new Blue();

export default Bluelagoon;
